import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'

import {
  listSystemConfig,
  updateSystemConfigValue,
  type SystemConfigEntry,
} from '@/api/system_config'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { dateFnsLocale } from '@/utils/dateLocale'

/**
 * /admin/system-config — редактор глобальных параметров системы.
 *
 * Только под admin (RLS на UPDATE и AdminOnly на роуте). Универсальный
 * KV-редактор: всё текстом, формат — на стороне читателя. Описание из
 * колонки `description` показывается под полем при редактировании.
 *
 * Inline: клик по ячейке «Значение» → input → Enter сохраняет, Esc отменяет.
 */
export function SystemConfigPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['system-config'],
    queryFn: listSystemConfig,
    staleTime: 30_000,
  })

  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (editingKey) inputRef.current?.focus()
  }, [editingKey])

  const mut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      updateSystemConfigValue(key, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['system-config'] })
      setEditingKey(null)
      setErrorMsg(null)
    },
    onError: (e: Error) => setErrorMsg(e.message),
  })

  function startEdit(row: SystemConfigEntry) {
    setEditingKey(row.key)
    setDraft(row.value)
    setErrorMsg(null)
  }

  function cancelEdit() {
    setEditingKey(null)
    setErrorMsg(null)
  }

  function commit(row: SystemConfigEntry) {
    const trimmed = draft.trim()
    if (trimmed === row.value) {
      cancelEdit()
      return
    }
    mut.mutate({ key: row.key, value: trimmed })
  }

  return (
    <div>
      <PageHeader
        title={t('settings.system.title')}
        subtitle={t('settings.system.subtitle')}
      />

      {isLoading ? (
        <Loader />
      ) : error ? (
        <ErrorBanner text={t('settings.error', { message: (error as Error).message })} />
      ) : !data || data.length === 0 ? (
        <ErrorBanner text={t('settings.system.notConfigured')} muted />
      ) : (
        <div className="overflow-hidden rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)]">
          <table className="w-full text-left">
            <thead className="bg-white/[0.03]">
              <tr className="text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
                <Th>{t('settings.system.col.key')}</Th>
                <Th>{t('settings.system.col.value')}</Th>
                <Th>{t('settings.system.col.description')}</Th>
                <Th>{t('settings.system.col.changedAt')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const isEditing = editingKey === row.key
                return (
                  <tr
                    key={row.key}
                    className="border-t border-[color:var(--stryzh-color-border-subtle)] align-top"
                  >
                    <Td>
                      <code className="font-mono text-sm text-[color:var(--stryzh-color-text-primary)]">
                        {row.key}
                      </code>
                    </Td>
                    <Td>
                      {isEditing ? (
                        <div className="flex flex-col gap-1">
                          <input
                            ref={inputRef}
                            type="text"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                commit(row)
                              } else if (e.key === 'Escape') {
                                e.preventDefault()
                                cancelEdit()
                              }
                            }}
                            disabled={mut.isPending}
                            className="rounded border border-[color:var(--stryzh-color-accent-orange)]/60 bg-[color:var(--stryzh-color-void)] px-2 py-1 font-mono text-sm text-[color:var(--stryzh-color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--stryzh-color-accent-orange)]/40"
                          />
                          {row.description ? (
                            <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                              {row.description}
                            </div>
                          ) : null}
                          {errorMsg && editingKey === row.key ? (
                            <div className="text-xs text-red-300">{errorMsg}</div>
                          ) : (
                            <div className="text-[10px] uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
                              {t('settings.system.editHint')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEdit(row)}
                          className="-mx-2 -my-1 inline-flex max-w-full rounded px-2 py-1 text-left font-mono text-sm text-[color:var(--stryzh-color-text-primary)] hover:bg-white/5"
                          title={t('settings.system.clickToEdit')}
                        >
                          {row.value}
                        </button>
                      )}
                    </Td>
                    <Td>
                      <span className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
                        {row.description ?? '—'}
                      </span>
                    </Td>
                    <Td>
                      <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                        {new Date(row.updated_at).toLocaleString('ru-RU')}
                      </div>
                      <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                        {formatDistanceToNow(new Date(row.updated_at), {
                          locale: dateFnsLocale(),
                          addSuffix: true,
                        })}
                      </div>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-xs font-medium">{children}</th>
}
function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-3 py-2">{children}</td>
}

function ErrorBanner({ text, muted }: { text: string; muted?: boolean }) {
  const cls = muted
    ? 'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]'
    : 'border-red-500/40 bg-red-500/10 text-red-200'
  return <div className={`rounded border p-4 text-sm ${cls}`}>{text}</div>
}
